import React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiBarChart2,
  FiCalendar,
  FiClipboard,
  FiPackage,
  FiShoppingCart,
  FiUsers,
} from "react-icons/fi";
import "@fontsource/montserrat/300.css";
import pilcoreLogo from "../assets/Logo_Pilcore_Vertical_Blanco.svg";
import {
  addBusinessUnitToPath,
  normalizeAllowedModules,
  resolveGlobalSettingsEntryPath,
  resolveModuleEntryPath,
} from "../utils/moduleEntryResolver";
import { usePmsTheme } from "./PmsThemeContext";
import { canAccessCorporatePurchasing } from "../corporate-purchasing/corporatePurchasingAccess";
import "./ModuleSelector.css";

const MODULE_SELECTOR_COPY = {
  en: {
    eyebrow: "Your Pilcore workspace",
    title: "Choose a module",
    subtitle: "Everything you need to manage your property, in one place.",
    empty: "There are no modules available for the active business unit.",
    moduleLabel: "Module",
    open: "Open",
    goToModule: (title) => `Go to ${title}`,
    globalSettings: "Global settings",
    usersRoles: "Users & Roles",
    chooseWorkspace: "Choose workspace",
    modules: {
      pms: {
        title: "Property Management System",
        description: "Manage reservations, availability and daily operations from one panel.",
      },
      purchasing: {
        title: "Purchasing & Inventory",
        description: "Control inventory, orders and purchasing approvals.",
      },
      pos: {
        title: "POS / Sales",
        description: "Review POS sales, manage products and prepare costing links.",
      },
      analytics: {
        title: "Management Control",
        description: "Analyze net sales, costs, margins, forecasts and purchasing control.",
      },
      onboarding: {
        title: "Onboarding",
        description: "Collect the initial hotel setup with guided forms ready for review.",
      },
      crm: {
        title: "Customer Relationship Management",
        description: "Manage contacts, companies, opportunities and commercial activity.",
      },
    },
  },
  es: {
    eyebrow: "Tu espacio de trabajo Pilcore",
    title: "Elige un módulo",
    subtitle: "Todo lo que necesitas para gestionar tu alojamiento, en un mismo lugar.",
    empty: "No hay módulos disponibles para la unidad de negocio activa.",
    moduleLabel: "Módulo",
    open: "Abrir",
    goToModule: (title) => `Ir al módulo ${title}`,
    globalSettings: "Configuración global",
    usersRoles: "Usuarios y roles",
    chooseWorkspace: "Elegir espacio",
    modules: {
      pms: {
        title: "Property Management System",
        description: "Gestiona reservas, disponibilidad y operativa diaria desde un solo panel.",
      },
      purchasing: {
        title: "Purchasing & Inventory",
        description: "Controla inventarios, pedidos y aprobaciones de compra.",
      },
      pos: {
        title: "POS / Ventas",
        description: "Revisa ventas POS, gestiona productos y prepara enlaces de costes.",
      },
      analytics: {
        title: "Control de Gestión",
        description: "Analiza ventas netas, costes, márgenes, previsiones y control de compras.",
      },
      onboarding: {
        title: "Onboarding",
        description: "Configura el alojamiento con formularios guiados y listos para revisar.",
      },
      crm: {
        title: "Customer Relationship Management",
        description: "Gestiona contactos, empresas, oportunidades y actividad comercial.",
      },
    },
  },
};

const MODULES = [
  {
    key: "pms",
    Icon: FiCalendar,
  },
  {
    key: "purchasing",
    Icon: FiPackage,
  },
  {
    key: "pos",
    Icon: FiShoppingCart,
  },
  {
    key: "analytics",
    Icon: FiBarChart2,
  },
  {
    key: "onboarding",
    Icon: FiClipboard,
  },
  {
    key: "crm",
    Icon: FiUsers,
  },
];

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "{}");
  } catch (_err) {
    return {};
  }
}

export default function ModuleSelector() {
  const navigate = useNavigate();
  const { languageId, themeId } = usePmsTheme();
  const user = readStoredUser();
  const copy = MODULE_SELECTOR_COPY[languageId] || MODULE_SELECTOR_COPY.en;
  const allowedModules = normalizeAllowedModules(user?.allowed_modules);
  const isAdmin = Number(user?.id) === 1;
  const activeBusinessUnitId = user?.active_business_unit_id;
  const globalSettingsTarget = addBusinessUnitToPath(
    resolveGlobalSettingsEntryPath(user?.permissions, { isAdmin, corporateAccess: canAccessCorporatePurchasing(user) }),
    activeBusinessUnitId
  );
  const visibleModules = MODULES
    .filter((moduleDefinition) => isAdmin || allowedModules.includes(moduleDefinition.key))
    .map((moduleDefinition) => ({
      ...moduleDefinition,
      target: resolveModuleEntryPath({
        moduleKey: moduleDefinition.key,
        permissions: user?.permissions || {},
        businessUnitId: activeBusinessUnitId,
        isAdmin,
      }),
    }))
    .filter((moduleDefinition) => Boolean(moduleDefinition.target));

  if (!activeBusinessUnitId || user?.has_business_unit_access === false) {
    return <Navigate to="/select-workspace" replace />;
  }

  return (
    <div className="auth-page module-selector-page" data-theme={themeId}>
      <div className="auth-shell">
        <div className="module-selector-panel">
          <header className="module-selector-header">
            <div className="module-selector-brand">
              <img src={pilcoreLogo} alt="Pilcore" className="module-selector-logo" />
            </div>
            <div className="auth-heading">
              <p className="module-selector-eyebrow">{copy.eyebrow}</p>
              <h1 className="auth-title">{copy.title}</h1>
              <p className="auth-subtitle">{copy.subtitle}</p>
            </div>
          </header>

          {visibleModules.length === 0 ? (
            <div className="module-selector-empty">{copy.empty}</div>
          ) : (
            <div className="module-selector-grid">
              {visibleModules.map((moduleDefinition, index) => {
                const moduleCopy = copy.modules[moduleDefinition.key] || {
                  title: moduleDefinition.key,
                  description: "",
                };
                const Icon = moduleDefinition.Icon;

                return (
                  <button
                    key={moduleDefinition.key}
                    type="button"
                    className="module-card"
                    onClick={() => navigate(moduleDefinition.target)}
                    aria-label={copy.goToModule(moduleCopy.title)}
                  >
                    <span className="module-card-icon" aria-hidden="true">
                      <Icon size={27} strokeWidth={1.65} />
                    </span>
                    <span className="module-card-content">
                      <span className="module-card-kicker">
                        {copy.moduleLabel} {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="module-card-title">{moduleCopy.title}</span>
                      <span className="module-card-description">{moduleCopy.description}</span>
                    </span>
                    <span className="module-card-action" aria-hidden="true">
                      <span className="module-card-action-label">{copy.open}</span>
                      <span className="module-card-arrow">
                        <FiArrowRight size={18} strokeWidth={1.8} />
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <Link className="module-selector-workspace-link" to="/select-workspace">
            <FiArrowLeft aria-hidden="true" />
            {copy.chooseWorkspace}
          </Link>
          {globalSettingsTarget && (
            <section className="module-selector-global-settings" aria-label={copy.globalSettings}>
              <span>{copy.globalSettings}</span>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(globalSettingsTarget)}
              >
                {copy.usersRoles}
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
